
import Layout from "@/components/layout/Layout";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { ArrowLeft, Edit, Trash, Handshake, Mail, Phone, MapPin, Building } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/components/ui/use-toast";

const ParceiroDetalhes = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Simulando dados de um parceiro (em uma implementação real, buscaríamos do banco de dados)
  const parceiro = {
    id: "1",
    nomeEmpresa: "Solar Tec Instalações Ltda",
    cnpj: "12.345.678/0001-90",
    responsavel: "João Silva",
    email: "contato@solartec.com.br",
    telefone: "(11) 98765-4321",
    endereco: "Rua da Energia, 123 - Centro",
    cidade: "São Paulo",
    estado: "SP",
    cep: "01234-567",
    tipoServico: "ambos",
    areaAtuacao: "Grande São Paulo, Campinas, Vale do Paraíba",
    observacoes: "Parceiro desde 2021, especializado em instalações residenciais e comerciais.",
    ativo: true,
    dataCadastro: "2021-05-15",
    ultimaAtualizacao: "2023-10-20",
  };
  
  const handleDelete = () => {
    toast({
      title: "Parceiro excluído",
      description: "O parceiro foi removido com sucesso!",
    });
    navigate("/parceiros");
  };
  
  const servicoLabel = {
    venda: "Apenas Vendas",
    instalacao: "Apenas Instalação",
    ambos: "Vendas e Instalação"
  };
  
  return (
    <Layout>
      <div className="container mx-auto p-6">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex items-center">
              <Button variant="outline" size="icon" asChild className="mr-4">
                <Link to="/parceiros">
                  <ArrowLeft className="h-4 w-4" />
                </Link>
              </Button>
              <div>
                <h1 className="text-3xl font-bold flex items-center">
                  <Handshake className="mr-2 h-8 w-8 text-energy-green" />
                  Detalhes do Parceiro
                </h1>
                <p className="text-muted-foreground">
                  Visualize as informações detalhadas do parceiro
                </p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" asChild>
                <Link to={`/parceiros/editar/${id}`}>
                  <Edit className="h-4 w-4 mr-2" />
                  Editar
                </Link>
              </Button>
              
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive">
                    <Trash className="h-4 w-4 mr-2" />
                    Excluir
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Você tem certeza?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Esta ação não pode ser desfeita. Isso excluirá permanentemente o parceiro do sistema.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete}>
                      Continuar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="md:col-span-2">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-xl flex items-center">
                      <Building className="mr-2 h-5 w-5 text-energy-green" />
                      {parceiro.nomeEmpresa}
                    </CardTitle>
                    <CardDescription>CNPJ: {parceiro.cnpj}</CardDescription>
                  </div>
                  <Badge variant={parceiro.ativo ? "default" : "outline"}>
                    {parceiro.ativo ? "Ativo" : "Inativo"}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">Informações Principais</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Responsável</p>
                      <p>{parceiro.responsavel}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Tipo de Serviço</p>
                      <p>{servicoLabel[parceiro.tipoServico as keyof typeof servicoLabel]}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Data de Cadastro</p>
                      <p>{new Date(parceiro.dataCadastro).toLocaleDateString('pt-BR')}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Última Atualização</p>
                      <p>{new Date(parceiro.ultimaAtualizacao).toLocaleDateString('pt-BR')}</p>
                    </div>
                  </div>
                </div>
                
                <Separator />
                
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">Área de Atuação</h3>
                  <p>{parceiro.areaAtuacao || "Não especificada"}</p>
                </div>
                
                <Separator />
                
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">Observações</h3>
                  <p>{parceiro.observacoes || "Nenhuma observação"}</p>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Informações de Contato</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start">
                  <Mail className="h-5 w-5 mr-2 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">E-mail</p>
                    <p className="text-sm">{parceiro.email}</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <Phone className="h-5 w-5 mr-2 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Telefone</p>
                    <p className="text-sm">{parceiro.telefone}</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <MapPin className="h-5 w-5 mr-2 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Endereço</p>
                    <p className="text-sm">{parceiro.endereco}</p>
                    <p className="text-sm">{parceiro.cidade}, {parceiro.estado}</p>
                    <p className="text-sm">CEP: {parceiro.cep}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ParceiroDetalhes;
